import React from "react";
import { Button, Tag } from "@jem-open/jem-ui";
import { Users, Paperclip } from "lucide-react";
import { useMcmAnnouncementDetailApiService } from "../services/api/McmAnnouncementQueryApiService";
import { getDisplayStatus, downloadMediaFile } from "../services/model/McmAnnouncementUtils";
import MultimessagingBreadcrumb from "../elements/MultimessagingBreadcrumb";

type MessageDetailViewProps = {
  announcementId: string;
  onGoBack: () => void;
  onCloseAnnouncement: () => void;
};

const statusMap: Record<string, { label: string; variant: "drafted" | "success" | "failed" | "processing" | "pending" }> = {
  draft: { label: "DRAFT", variant: "drafted" },
  scheduled: { label: "SCHEDULED", variant: "processing" },
  live: { label: "LIVE", variant: "success" },
  expired: { label: "EXPIRED", variant: "failed" },
  closed: { label: "CLOSED", variant: "failed" },
};

const MessageDetailView: React.FC<MessageDetailViewProps> = ({ announcementId, onGoBack, onCloseAnnouncement }) => {
  const { data: message, isLoading, isError } = useMcmAnnouncementDetailApiService(announcementId);

  if (isLoading) {
    return (
      <div>
        <MultimessagingBreadcrumb
          items={[
            { label: "Multimessaging", onClick: onGoBack },
            { label: "Loading..." },
          ]}
        />
        <p className="text-sm text-greyscale-text-caption py-md">Loading announcement details...</p>
      </div>
    );
  }

  if (isError || !message) {
    return (
      <div>
        <MultimessagingBreadcrumb
          items={[
            { label: "Multimessaging", onClick: onGoBack },
            { label: "Error" },
          ]}
        />
        <p className="text-sm text-red-500 py-md">
          Failed to load announcement details. Please try again.
        </p>
        <Button variant="outline" onClick={onGoBack}>
          Go Back
        </Button>
      </div>
    );
  }

  const displayStatus = getDisplayStatus(message);
  const statusInfo = statusMap[displayStatus] || { label: displayStatus.toUpperCase(), variant: "drafted" };

  return (
    <div>
      <MultimessagingBreadcrumb
        items={[
          { label: "Multimessaging", onClick: onGoBack },
          { label: message.title },
        ]}
      />

      <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
        Message details
      </h2>

      <div className="flex flex-col gap-lg max-w-[850px]">
        {/* Title */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Title</p>
          <p className="text-sm text-greyscale-text-caption mt-xxxs">{message.title}</p>
        </div>

        {/* Template */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Template</p>
          <p className="text-sm text-greyscale-text-caption mt-xxxs">
            {message.announcement_type?.name || "\u2014"}
          </p>
        </div>

        {/* Message */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Message</p>
          <p className="text-sm text-greyscale-text-caption mt-xxxs">{message.content || "\u2014"}</p>
        </div>

        {/* Companies sent to */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Companies sent to</p>
          <span className="text-sm text-greyscale-text-caption mt-xxxs">
            {message.target_employer_count} {message.target_employer_count === 1 ? "company" : "companies"}
          </span>
          {message.target_employers && message.target_employers.length > 0 && (
            <div className="flex flex-wrap gap-xxs mt-xxs">
              {message.target_employers.map((t) => (
                <Tag key={t.id} variant="pink">
                  {t.company_name}
                </Tag>
              ))}
            </div>
          )}
        </div>

        {/* Total recipients */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Total recipients</p>
          <div className="flex items-center gap-xxs mt-xxxs">
            <Users size={14} className="text-greyscale-text-caption" />
            <span className="text-sm text-greyscale-text-caption">
              {message.announcements_read_count}/{message.audience_snapshot_total} employees
            </span>
          </div>
        </div>

        {/* Sent / Scheduled / Close dates */}
        <div className="grid grid-cols-3 gap-lg">
          <div>
            <p className="text-sm font-semibold text-greyscale-text-title">Sent date</p>
            <p className="text-sm text-greyscale-text-caption mt-xxxs">
              {message.date_published
                ? new Date(message.date_published).toLocaleString()
                : "\u2014"}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-greyscale-text-title">Scheduled close</p>
            <p className="text-sm text-greyscale-text-caption mt-xxxs">
              {message.scheduled_end_date
                ? new Date(message.scheduled_end_date).toLocaleString()
                : "\u2014"}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-greyscale-text-title">Closed date</p>
            <p className="text-sm text-greyscale-text-caption mt-xxxs">
              {message.date_closed
                ? new Date(message.date_closed).toLocaleString()
                : "\u2014"}
            </p>
          </div>
        </div>

        {/* Sent / Created by */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">
            {message.published_by ? "Sent by" : "Created by"}
          </p>
          {(() => {
            const actor = message.published_by ?? message.created_by;
            return (
              <p className="text-sm text-greyscale-text-caption mt-xxxs">
                {actor?.first_name || actor?.last_name
                  ? [actor.first_name, actor.last_name].filter(Boolean).join(" ")
                  : "\u2014"}
              </p>
            );
          })()}
        </div>

        {/* Attachment */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Attachment</p>
          <div className="flex items-center gap-xxs mt-xxxs">
            <Paperclip size={14} className="text-greyscale-text-caption" />
            {message.media_url ? (
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                onClick={() => downloadMediaFile(message.media_url as string)}
              >
                {decodeURIComponent(message.media_url.split("/").pop()?.split("?")[0] || "Download attachment")}
              </button>
            ) : (
              <span className="text-sm text-greyscale-text-caption">None</span>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Status</p>
          <div className="mt-xxxs">
            <Tag variant={statusInfo.variant}>
              {statusInfo.label}
            </Tag>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-greyscale-border mt-lg" />
      <div className="flex items-center justify-between py-lg">
        <Button variant="outline" onClick={onGoBack}>
          Go Back
        </Button>
        {displayStatus === "live" && (
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={onCloseAnnouncement}
          >
            Close Announcement
          </Button>
        )}
      </div>
    </div>
  );
};

export default MessageDetailView;
